/**
 * TabsLayout Component
 * Renders tab-based layout (tabs-left or tabs-top)
 */

import React, { ReactNode } from 'react';
import './TabsLayout.css';
import { FormContainerConfig } from '../types/index';
import { ValidationBadge } from './ValidationBadge';

export interface TabsLayoutProps {
  config: FormContainerConfig;
  activeIndex: number;
  onTabChange: (index: number) => void;
  errors: Record<string, any[]>;
  children: ReactNode;
  theme?: 'dark' | 'light';
}

/**
 * Tab-based layout component
 * Supports tabs-left and tabs-top orientations
 */
export const TabsLayout: React.FC<TabsLayoutProps> = ({
  config,
  activeIndex,
  onTabChange,
  errors,
  children,
  theme = 'light',
}) => {
  const isLeftLayout = config.layout === 'tabs-left';
  const tabWidth = config.layoutConfig?.tabWidth || '200px';

  return (
    <div
      className={`af-tabs-layout af-tabs-layout--${isLeftLayout ? 'left' : 'top'}`}
      style={{ '--af-tab-width': tabWidth } as React.CSSProperties}
    >
      <div className={`af-tabs-layout__tabs af-tabs-layout__tabs--${isLeftLayout ? 'left' : 'top'}`}>
        {config.childForms.map((child, index) => {
          const errorCount = (errors[child.formID] || []).length;
          const isActive = index === activeIndex;

          return (
            <button
              key={child.formID}
              type="button"
              className={`af-tabs-layout__tab${isActive ? ' af-tabs-layout__tab--active' : ''}`}
              onClick={() => onTabChange(index)}
              disabled={config.validationMode === 'blocked' && index > activeIndex}
            >
              <span className="af-tabs-layout__tab-label">{child.label}</span>
              <ValidationBadge count={errorCount} />
            </button>
          );
        })}
      </div>

      <div className="af-tabs-layout__content">
        {children}
      </div>
    </div>
  );
};

TabsLayout.displayName = 'TabsLayout';
